import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./CameraAnalyticsPage.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REALTIME_ROW_LIMIT = 30;
const VEHICLE_TYPES = ["auto", "motorbike", "bus", "car", "truck"];

// Realtime table is intentionally limited to 30 rows only.
// Aggregate tables have their own independent pagination and are NOT capped at 30.
const AGGREGATE_PAGE_SIZE = {
  minute: 100,
  hourly: 500,
  daily: 500,
};

const CAMERA_LOCATIONS = [
  {
    id: "camera-site-1",
    place: "Ettayapuram Rd",
    location: "Perison Plaza Signal",
    boxes: 2,
    cameras: 2,
    lat: 8.81277,
    lng: 78.13647,
  },
  {
    id: "camera-site-2",
    place: "4th Gate",
    location: "South side of Milavittan Road",
    boxes: 1,
    cameras: 1,
    lat: 8.80967,
    lng: 78.1335,
  },
  {
    id: "camera-site-3",
    place: "International Furniture Park",
    location: "Madurai - Tiruchendur Rd highway east side",
    boxes: 1,
    cameras: 1,
    lat: 8.81624,
    lng: 78.08118,
  },
  {
    id: "camera-site-4",
    place: "3rd Mile",
    location: "Near TVL-TUT-MDR Bypass Bridge",
    boxes: 2,
    cameras: 2,
    lat: 8.789608,
    lng: 78.11493,
  },
  {
    id: "camera-site-5",
    place: "Alageshapuram Road",
    location: "Roundana near St. Thomas School",
    boxes: 1,
    cameras: 2,
    lat: 8.81705,
    lng: 78.14797,
  },
  {
    id: "camera-site-6",
    place: "Tiruchendur Rd",
    location: "Muthiahpuram",
    boxes: 1,
    cameras: 1,
    lat: 8.74541,
    lng: 78.1253,
  },
];

function localDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function numberSafe(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatNumber(value) {
  return numberSafe(value).toLocaleString("en-IN");
}

function emptyTotals() {
  return {
    auto: 0,
    motorbike: 0,
    bus: 0,
    car: 0,
    truck: 0,
    total: 0,
  };
}

function normalizeCameraRow(row) {
  const auto = numberSafe(row.auto);
  const motorbike = numberSafe(row.motorbike);
  const bus = numberSafe(row.bus);
  const car = numberSafe(row.car);
  const truck = numberSafe(row.truck);

  return {
    id:
      row._id ||
      row.dedupeKey ||
      `${row.cameraId || "camera"}-${row.timestampText || row.timestamp}-${row.direction}`,
    dedupeKey: row.dedupeKey || "",
    sourceFile: row.sourceFile || "",
    cameraId: row.cameraId || "ALL",
    cameraName: row.cameraName || row.place || "",
    location: row.location || "",
    timestamp: row.timestampText || row.timestamp || "",
    timestampIso: row.timestamp || "",
    direction: row.direction || "All Directions",
    auto,
    motorbike,
    bus,
    car,
    truck,
    total: numberSafe(row.total) || auto + motorbike + bus + car + truck,
  };
}

function getRowSortTime(row) {
  const isoTime = row.timestampIso ? new Date(row.timestampIso).getTime() : NaN;
  if (Number.isFinite(isoTime)) return isoTime;

  const text = String(row.timestamp || "");
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(text)) {
    const indiaTime = new Date(text.replace(" ", "T") + "+05:30").getTime();
    if (Number.isFinite(indiaTime)) return indiaTime;
  }

  const localTime = new Date(text.replace(" ", "T")).getTime();
  return Number.isFinite(localTime) ? localTime : 0;
}

function mergeLatestCameraRows(existingRows, incomingRows, maxRows = REALTIME_ROW_LIMIT) {
  const map = new Map();

  [...incomingRows, ...existingRows].forEach((row) => {
    const key =
      row.dedupeKey ||
      row.id ||
      `${row.cameraId}-${row.timestamp}-${row.direction}-${row.sourceFile}`;

    if (!map.has(key)) map.set(key, row);
  });

  return Array.from(map.values())
    .sort((a, b) => getRowSortTime(b) - getRowSortTime(a))
    .slice(0, maxRows);
}

function sumRows(rows) {
  return (rows || []).reduce(
    (acc, row) => {
      acc.auto += numberSafe(row.auto);
      acc.motorbike += numberSafe(row.motorbike);
      acc.bus += numberSafe(row.bus);
      acc.car += numberSafe(row.car);
      acc.truck += numberSafe(row.truck);
      acc.total +=
        numberSafe(row.total) ||
        numberSafe(row.auto) +
          numberSafe(row.motorbike) +
          numberSafe(row.bus) +
          numberSafe(row.car) +
          numberSafe(row.truck);
      return acc;
    },
    emptyTotals()
  );
}

function totalFromCounts(counts) {
  return VEHICLE_TYPES.reduce((sum, type) => sum + numberSafe(counts?.[type]), 0);
}

function buildDirectionData(rows) {
  const grouped = {};

  (rows || []).forEach((row) => {
    const direction = row.direction || "All Directions";

    if (!grouped[direction]) {
      grouped[direction] = {
        direction,
        auto: 0,
        motorbike: 0,
        bus: 0,
        car: 0,
        truck: 0,
      };
    }

    VEHICLE_TYPES.forEach((type) => {
      grouped[direction][type] += numberSafe(row[type]);
    });
  });

  return Object.values(grouped)
    .map((item) => ({ ...item, total: totalFromCounts(item) }))
    .sort((a, b) => b.total - a.total);
}

function CameraMapFlyTo({ selectedLocation }) {
  const map = useMap();

  useEffect(() => {
    if (!selectedLocation) return;
    map.flyTo([selectedLocation.lat, selectedLocation.lng], 16, { duration: 0.8 });
  }, [map, selectedLocation]);

  return null;
}

function SummaryCards({ totals, sourceLabel, totalLabel = "Total Vehicles" }) {
  const safe = totals || emptyTotals();

  return (
    <section className="camera-summary-grid camera-analysis-summary-grid">
      <div className="camera-summary-card total">
        <span>{totalLabel}</span>
        <strong>{formatNumber(safe.total)}</strong>
        {sourceLabel ? <small>{sourceLabel}</small> : null}
      </div>

      <div className="camera-summary-card">
        <span>Cars</span>
        <strong>{formatNumber(safe.car)}</strong>
      </div>

      <div className="camera-summary-card">
        <span>Motorbikes</span>
        <strong>{formatNumber(safe.motorbike)}</strong>
      </div>

      <div className="camera-summary-card">
        <span>Buses</span>
        <strong>{formatNumber(safe.bus)}</strong>
      </div>

      <div className="camera-summary-card">
        <span>Trucks</span>
        <strong>{formatNumber(safe.truck)}</strong>
      </div>

      <div className="camera-summary-card">
        <span>Autos</span>
        <strong>{formatNumber(safe.auto)}</strong>
      </div>
    </section>
  );
}

function TimeBarChart({ rows, labelMode = "hour" }) {
  const data = rows || [];
  const max = Math.max(...data.map((item) => numberSafe(item.total)), 0);

  if (!data.length) {
    return <div className="camera-analysis-empty">No data available for this selection.</div>;
  }

  return (
    <div className="camera-analysis-bar-chart">
      {data.map((item, index) => {
        const total = numberSafe(item.total);
        const height = max ? Math.max((total / max) * 100, 2) : 0;
        const text = String(item.periodStart || "");
        let label = text;

        if (labelMode === "hour") label = text.slice(11, 16) || text;
        if (labelMode === "minute") label = text.slice(11, 16) || text;
        if (labelMode === "day") label = text.slice(5, 10) || text;

        const showLabel =
          data.length <= 24 ||
          index === 0 ||
          index === data.length - 1 ||
          index % Math.max(Math.ceil(data.length / 12), 1) === 0;

        return (
          <div
            className="camera-analysis-bar-item"
            key={`${item.periodStart}-${index}`}
            title={`${item.periodStart}: ${formatNumber(total)}`}
          >
            <span className="camera-analysis-bar-value">{formatNumber(total)}</span>
            <div className="camera-analysis-bar-track">
              <div className="camera-analysis-bar-fill" style={{ height: `${height}%` }} />
            </div>
            <span className="camera-analysis-bar-label">{showLabel ? label : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function AggregateTable({ title, granularity, data, page, onPageChange }) {
  const rows = data?.rows || [];
  const pagination = data?.pagination;

  return (
    <section className="camera-panel camera-analysis-table-panel">
      <div className="camera-panel-header">
        <div>
          <h2>{title}</h2>
          <p>Direction-wise vehicle counts stored centrally in AWS RDS PostgreSQL.</p>
        </div>

        <div className="camera-latest-badge camera-analysis-source-badge">
          {pagination
            ? `${formatNumber(pagination.totalRows)} aggregate rows`
            : "RDS data"}
        </div>
      </div>

      <div className="camera-table-info">
        <span>
          Aggregate data is stored in AWS RDS PostgreSQL after being pushed by the camera machine.
        </span>
        <span>
          {pagination
            ? `Page ${pagination.page}/${pagination.totalPages}`
            : ""}
        </span>
      </div>

      <div className="camera-table-wrap camera-analysis-table-wrap">
        <table className="camera-table camera-analysis-table">
          <thead>
            <tr>
              <th>
                {granularity === "daily"
                  ? "Day"
                  : granularity === "hourly"
                  ? "Hour"
                  : "Minute"}
              </th>
              <th>Camera</th>
              <th>Direction</th>
              <th>Auto</th>
              <th>Motorbike</th>
              <th>Bus</th>
              <th>Car</th>
              <th>Truck</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => (
                <tr
                  key={
                    row.id ||
                    `${row.cameraId}-${row.periodStart}-${row.direction}-${index}`
                  }
                >
                  <td>{row.periodStart}</td>
                  <td>{row.cameraName || row.cameraId}</td>
                  <td>{row.direction}</td>
                  <td>{formatNumber(row.auto)}</td>
                  <td>{formatNumber(row.motorbike)}</td>
                  <td>{formatNumber(row.bus)}</td>
                  <td>{formatNumber(row.car)}</td>
                  <td>{formatNumber(row.truck)}</td>
                  <td className="camera-analysis-total-cell">{formatNumber(row.total)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="camera-analysis-empty-cell">
                  No {granularity} records are available for the selected date/location.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination ? (
        <div className="camera-analysis-pagination">
          <span>
            Page {pagination.page} of {pagination.totalPages} · {formatNumber(pagination.totalRows)} total rows · {pagination.limit} rows/page
          </span>
          <div>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(page - 1, 1))}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pagination.totalPages}
              onClick={() => onPageChange(Math.min(page + 1, pagination.totalPages))}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function LocationCountAnalysis({ dashboard }) {
  const locations = dashboard?.locations || [];
  const directions = dashboard?.directions || [];
  const maxLocation = Math.max(...locations.map((item) => numberSafe(item.total)), 0);
  const maxDirection = Math.max(...directions.map((item) => numberSafe(item.total)), 0);

  return (
    <section className="camera-analysis-two-col">
      <div className="camera-panel camera-analysis-card">
        <div className="camera-panel-header">
          <div>
            <h2>All Location Count Analysis</h2>
            <p>Daily total comparison across every camera location.</p>
          </div>
        </div>

        <div className="camera-analysis-ranking">
          {locations.length ? (
            locations.map((item, index) => {
              const width = maxLocation
                ? Math.max((numberSafe(item.total) / maxLocation) * 100, 2)
                : 0;

              return (
                <div className="camera-analysis-rank-row" key={item.cameraId}>
                  <div className="camera-analysis-rank-number">{index + 1}</div>
                  <div className="camera-analysis-rank-main">
                    <div className="camera-analysis-rank-label">
                      <div>
                        <strong>{item.cameraName}</strong>
                        <span>{item.location}</span>
                      </div>
                      <b>{formatNumber(item.total)}</b>
                    </div>
                    <div className="camera-analysis-rank-track">
                      <div
                        className="camera-analysis-rank-fill"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="camera-analysis-empty">No daily location data available.</div>
          )}
        </div>
      </div>

      <div className="camera-panel camera-analysis-card">
        <div className="camera-panel-header">
          <div>
            <h2>Direction Analysis</h2>
            <p>Daily vehicle counts grouped by direction across all locations.</p>
          </div>
        </div>

        <div className="camera-analysis-ranking">
          {directions.length ? (
            directions.map((item, index) => {
              const width = maxDirection
                ? Math.max((numberSafe(item.total) / maxDirection) * 100, 2)
                : 0;

              return (
                <div className="camera-analysis-rank-row" key={`${item.direction}-${index}`}>
                  <div className="camera-analysis-rank-main">
                    <div className="camera-analysis-rank-label">
                      <strong>{item.direction}</strong>
                      <b>{formatNumber(item.total)}</b>
                    </div>
                    <div className="camera-analysis-rank-track">
                      <div
                        className="camera-analysis-rank-fill camera-analysis-rank-fill-purple"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="camera-analysis-empty">No daily direction data available.</div>
          )}
        </div>
      </div>
    </section>
  );
}

function AllLocationMap({ dashboard }) {
  const rows = dashboard?.locations || [];
  const valid = rows.filter(
    (item) => Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng))
  );
  const center = valid.length
    ? [Number(valid[0].lat), Number(valid[0].lng)]
    : [8.7642, 78.1348];
  const max = Math.max(...valid.map((item) => numberSafe(item.total)), 0);

  return (
    <section className="camera-panel camera-analysis-map-panel">
      <div className="camera-panel-header">
        <div>
          <h2>All Location Daily Count Map</h2>
          <p>Marker size is proportional to the daily vehicle count.</p>
        </div>
      </div>

      <MapContainer center={center} zoom={11} className="camera-analysis-map" scrollWheelZoom>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {valid.map((item) => {
          const ratio = max ? numberSafe(item.total) / max : 0;
          const radius = 8 + ratio * 18;

          return (
            <CircleMarker
              key={item.cameraId}
              center={[Number(item.lat), Number(item.lng)]}
              radius={radius}
              pathOptions={{ fillOpacity: 0.75, weight: 2 }}
            >
              <Popup>
                <div className="camera-popup">
                  <h3>{item.cameraName}</h3>
                  <p>{item.location}</p>
                  <p><strong>Total:</strong> {formatNumber(item.total)}</p>
                  <p><strong>Cars:</strong> {formatNumber(item.car)}</p>
                  <p><strong>Motorbikes:</strong> {formatNumber(item.motorbike)}</p>
                  <p><strong>Buses:</strong> {formatNumber(item.bus)}</p>
                  <p><strong>Trucks:</strong> {formatNumber(item.truck)}</p>
                  <p><strong>Autos:</strong> {formatNumber(item.auto)}</p>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </section>
  );
}

export default function CameraAnalyticsPage({ navigate }) {
  // ---------------------------------------------------------------------------
  // REALTIME STATE - the only section intentionally limited to 20 rows.
  // ---------------------------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [sourceLabel, setSourceLabel] = useState("Connecting to backend...");
  const [loadingRealtime, setLoadingRealtime] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(CAMERA_LOCATIONS[0]);
  const [connectionError, setConnectionError] = useState("");

  // ---------------------------------------------------------------------------
  // AGGREGATE STATE - read on demand from minute/hourly/daily CSV files.
  // ---------------------------------------------------------------------------
  const [selectedDate, setSelectedDate] = useState(localDateString());
  const [aggregateCameraId, setAggregateCameraId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [allLocationDashboard, setAllLocationDashboard] = useState(null);
  const [minuteData, setMinuteData] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [minutePage, setMinutePage] = useState(1);
  const [hourlyPage, setHourlyPage] = useState(1);
  const [dailyPage, setDailyPage] = useState(1);
  const [aggregateLoading, setAggregateLoading] = useState(false);
  const [aggregateError, setAggregateError] = useState("");
  const [aggregateRefreshToken, setAggregateRefreshToken] = useState(0);

  const fetchCameraData = useCallback(async () => {
    try {
      if (!BACKEND) throw new Error("REACT_APP_BACKEND_URL is missing");

      const res = await axios.get(`${BACKEND}/api/cameradata/latest`, {
        params: { limit: REALTIME_ROW_LIMIT },
      });

      const backendRows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.rows)
        ? res.data.rows
        : [];

      const normalizedRows = backendRows.map(normalizeCameraRow);
      setRows((prev) =>
        mergeLatestCameraRows(prev, normalizedRows, REALTIME_ROW_LIMIT)
      );

      setSourceLabel(
        normalizedRows.length ? "Live realtime data" : "Waiting for camera CSV data"
      );
      setConnectionError("");
    } catch (e) {
      setConnectionError(e.response?.data?.message || e.message || "Failed to load camera data");
      setSourceLabel("Backend not reachable");
    } finally {
      setLoadingRealtime(false);
    }
  }, []);

  useEffect(() => {
    fetchCameraData();
    if (!BACKEND) return undefined;

    const socket = io(BACKEND, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => {
      setConnectionError("");
      setSourceLabel("Live realtime data");
    });

    socket.on("connect_error", () => {
      setSourceLabel("Backend polling mode");
    });

    socket.on("new-camera-data", (payload) => {
      const incoming = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.rows)
        ? payload.rows
        : payload
        ? [payload]
        : [];

      if (!incoming.length) return;

      const normalized = incoming.map(normalizeCameraRow);
      setRows((prev) => mergeLatestCameraRows(prev, normalized, REALTIME_ROW_LIMIT));
      setSourceLabel("Live camera update");
      setLoadingRealtime(false);
      setConnectionError("");
    });

    const refreshTimer = setInterval(fetchCameraData, 30_000);

    return () => {
      clearInterval(refreshTimer);
      socket.disconnect();
    };
  }, [fetchCameraData]);

  useEffect(() => {
    setMinutePage(1);
    setHourlyPage(1);
    setDailyPage(1);
  }, [selectedDate, aggregateCameraId]);

  const loadDashboardData = useCallback(async () => {
    if (!BACKEND) return;

    try {
      setAggregateLoading(true);
      setAggregateError("");

      const selectedParams = {
        date: selectedDate,
        ...(aggregateRefreshToken ? { refresh: "1" } : {}),
      };
      if (aggregateCameraId) selectedParams.cameraId = aggregateCameraId;

      const [selectedRes, allRes] = await Promise.all([
        axios.get(`${BACKEND}/api/cameradata/dashboard`, { params: selectedParams }),
        axios.get(`${BACKEND}/api/cameradata/dashboard`, {
          params: {
            date: selectedDate,
            ...(aggregateRefreshToken ? { refresh: "1" } : {}),
          },
        }),
      ]);

      setDashboard(selectedRes.data);
      setAllLocationDashboard(allRes.data);
    } catch (e) {
      setAggregateError(
        e.response?.data?.message || e.message || "Failed to load aggregate dashboard"
      );
    } finally {
      setAggregateLoading(false);
    }
  }, [selectedDate, aggregateCameraId, aggregateRefreshToken]);

  const loadGranularity = useCallback(
    async (granularity, page, setter) => {
      if (!BACKEND) return;

      try {
        const params = {
          granularity,
          date: selectedDate,
          page,
          limit: AGGREGATE_PAGE_SIZE[granularity],
          ...(aggregateRefreshToken ? { refresh: "1" } : {}),
        };
        if (aggregateCameraId) params.cameraId = aggregateCameraId;

        const res = await axios.get(`${BACKEND}/api/cameradata/aggregate`, { params });
        setter(res.data);
      } catch (e) {
        setAggregateError(
          e.response?.data?.message ||
            e.message ||
            `Failed to load ${granularity} data`
        );
      }
    },
    [selectedDate, aggregateCameraId, aggregateRefreshToken]
  );

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData, aggregateRefreshToken]);

  useEffect(() => {
    loadGranularity("minute", minutePage, setMinuteData);
  }, [loadGranularity, minutePage, aggregateRefreshToken]);

  useEffect(() => {
    loadGranularity("hourly", hourlyPage, setHourlyData);
  }, [loadGranularity, hourlyPage, aggregateRefreshToken]);

  useEffect(() => {
    loadGranularity("daily", dailyPage, setDailyData);
  }, [loadGranularity, dailyPage, aggregateRefreshToken]);

  const latestRows = useMemo(
    () => rows.slice(0, REALTIME_ROW_LIMIT),
    [rows]
  );

  const selectedRows = useMemo(() => {
    const hasCameraSpecificRows = latestRows.some(
      (row) => row.cameraId && row.cameraId !== "ALL"
    );

    if (!hasCameraSpecificRows) return latestRows;
    return latestRows.filter((row) => row.cameraId === selectedLocation.id);
  }, [latestRows, selectedLocation]);

  const totalsByCamera = useMemo(() => {
    const grouped = {};

    latestRows.forEach((row) => {
      const key = row.cameraId || "ALL";
      if (!grouped[key]) grouped[key] = emptyTotals();
      VEHICLE_TYPES.forEach((type) => {
        grouped[key][type] += numberSafe(row[type]);
      });
      grouped[key].total += numberSafe(row.total);
    });

    return grouped;
  }, [latestRows]);

  const realtimeTotals = useMemo(() => sumRows(latestRows), [latestRows]);
  const directionData = useMemo(() => buildDirectionData(selectedRows), [selectedRows]);
  const latestRow = latestRows[0];

  const aggregateLocationLabel = useMemo(() => {
    if (!aggregateCameraId) return "All locations";
    return (
      CAMERA_LOCATIONS.find((item) => item.id === aggregateCameraId)?.place ||
      aggregateCameraId
    );
  }, [aggregateCameraId]);

  return (
    <div className="camera-page">
      <div className="camera-bg-glow camera-bg-glow-one" />
      <div className="camera-bg-glow camera-bg-glow-two" />

      <header className="camera-navbar">
        <button className="camera-back-button" onClick={() => navigate?.("/")}>
          ← Home
        </button>

        <div>
          <h1>Camera Data</h1>
          <p>
            Realtime latest {REALTIME_ROW_LIMIT} rows + minute, hourly and daily count analysis
          </p>
        </div>

        <button className="camera-track-button" onClick={() => navigate?.("/tracking")}>
          Bus Tracking
        </button>
      </header>

      <main className="camera-content">
        {/* ================================================================== */}
        {/* REALTIME SECTION - SAME STYLE AS EXISTING PAGE, LATEST 20 ONLY.    */}
        {/* ================================================================== */}
        <div id="realtime" className="camera-analysis-section-title">
          <div>
            <span>REALTIME</span>
            <h2>Live Camera Counts</h2>
          </div>
          <div className="camera-latest-badge">Latest {REALTIME_ROW_LIMIT} rows only</div>
        </div>

        <SummaryCards
          totals={realtimeTotals}
          sourceLabel={sourceLabel}
          totalLabel="Total Vehicles"
        />

        {connectionError ? (
          <div className="camera-live-warning">{connectionError}</div>
        ) : null}

        <section className="camera-map-layout">
          <aside className="camera-location-panel">
            <div className="camera-location-header">
              <h2>Camera Locations</h2>
              <p>
                {CAMERA_LOCATIONS.reduce((sum, item) => sum + item.cameras, 0)} cameras across {CAMERA_LOCATIONS.length} locations
              </p>
            </div>

            <div className="camera-location-list">
              {CAMERA_LOCATIONS.map((item) => {
                const cameraTotal = totalFromCounts(totalsByCamera[item.id] || {});

                return (
                  <button
                    key={item.id}
                    className={
                      selectedLocation.id === item.id
                        ? "camera-location-item active"
                        : "camera-location-item"
                    }
                    onClick={() => setSelectedLocation(item)}
                  >
                    <div>
                      <strong>{item.place}</strong>
                      <span>{item.location}</span>
                    </div>
                    <small>{formatNumber(cameraTotal)} vehicles</small>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="camera-map-card">
            <MapContainer
              center={[selectedLocation.lat, selectedLocation.lng]}
              zoom={14}
              className="camera-osm-map"
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <CameraMapFlyTo selectedLocation={selectedLocation} />

              {CAMERA_LOCATIONS.map((item) => {
                const cameraTotal = totalFromCounts(totalsByCamera[item.id] || {});
                return (
                  <Marker
                    key={item.id}
                    position={[item.lat, item.lng]}
                    eventHandlers={{ click: () => setSelectedLocation(item) }}
                  >
                    <Popup>
                      <div className="camera-popup">
                        <h3>{item.place}</h3>
                        <p>{item.location}</p>
                        <p><strong>Cameras:</strong> {item.cameras}</p>
                        <p><strong>Latest Vehicles:</strong> {formatNumber(cameraTotal)}</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </section>
        </section>

        <section className="camera-selected-panel">
          <div>
            <h2>{selectedLocation.place}</h2>
            <p>{selectedLocation.location}</p>
          </div>

          <div className="camera-selected-meta">
            <span>{selectedLocation.boxes} boxes</span>
            <span>{selectedLocation.cameras} cameras</span>
            <span>{selectedLocation.lat}, {selectedLocation.lng}</span>
          </div>
        </section>

        <section className="camera-direction-grid">
          {directionData.slice(0, 4).map((item) => (
            <div className="camera-direction-card" key={item.direction}>
              <span>{item.direction}</span>
              <strong>{formatNumber(item.total)}</strong>
              <small>
                Car {formatNumber(item.car)} | Bike {formatNumber(item.motorbike)} | Bus {formatNumber(item.bus)}
              </small>
            </div>
          ))}

          {!loadingRealtime && directionData.length === 0 ? (
            <div className="camera-direction-card">
              <span>No direction data</span>
              <strong>0</strong>
              <small>Waiting for CSV rows</small>
            </div>
          ) : null}
        </section>

        {/* <section className="camera-panel">
          <div className="camera-panel-header">
            <div>
              <h2>Vehicle Count Table</h2>
              <p>
                {loadingRealtime
                  ? "Loading camera data..."
                  : latestRow
                  ? `Latest timestamp: ${latestRow.timestamp}`
                  : "No camera records available"}
              </p>
            </div>

            <div className="camera-latest-badge">Latest {REALTIME_ROW_LIMIT} rows</div>
          </div>

          <div className="camera-table-info">
            <span>Showing realtime rows only. Data is not saved in backend database.</span>
            <span>Rows: {latestRows.length}/{REALTIME_ROW_LIMIT}</span>
          </div>

          <div className="camera-table-wrap camera-realtime-30-wrap">
            <table className="camera-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Camera</th>
                  <th>Direction</th>
                  <th>Auto</th>
                  <th>Motorbike</th>
                  <th>Bus</th>
                  <th>Car</th>
                  <th>Truck</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {latestRows.map((row, index) => (
                  <tr key={row.dedupeKey || row.id || `${row.timestamp}-${index}`}>
                    <td>{row.timestamp}</td>
                    <td>{row.cameraName || row.cameraId}</td>
                    <td>{row.direction}</td>
                    <td>{formatNumber(row.auto)}</td>
                    <td>{formatNumber(row.motorbike)}</td>
                    <td>{formatNumber(row.bus)}</td>
                    <td>{formatNumber(row.car)}</td>
                    <td>{formatNumber(row.truck)}</td>
                    <td className="camera-analysis-total-cell">{formatNumber(row.total)}</td>
                  </tr>
                ))}

                {!loadingRealtime && latestRows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="camera-analysis-empty-cell">
                      No realtime camera data available.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section> */}

        {/* ================================================================== */}
        {/* AGGREGATE CONTROLS - APPLY TO MINUTE / HOURLY / DAILY ONLY.        */}
        {/* ================================================================== */}
        <div className="camera-analysis-major-divider" />

        <section className="camera-panel camera-analysis-filter-panel">
          <div className="camera-analysis-filter-copy">
            <span className="camera-analysis-kicker">HISTORICAL COUNT ANALYSIS</span>
            <h2>Minute, Hourly & Daily CSV Data</h2>
            <p>
              Minute, hourly and daily CSV changes are pushed by the camera machine and stored centrally in AWS RDS PostgreSQL. The frontend reads this historical data from the backend; it never connects to the private camera machine.
            </p>
          </div>

          <div className="camera-analysis-filters">
            <label>
              Date
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </label>

            <label>
              Location
              <select
                value={aggregateCameraId}
                onChange={(e) => setAggregateCameraId(e.target.value)}
              >
                <option value="">All locations</option>
                {CAMERA_LOCATIONS.map((item) => (
                  <option key={item.id} value={item.id}>{item.place}</option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="camera-track-button camera-analysis-refresh-button"
              onClick={() => setAggregateRefreshToken((value) => value + 1)}
            >
              Refresh Data
            </button>
          </div>
        </section>

        {aggregateError ? (
          <div className="camera-live-warning camera-analysis-error">
            {aggregateError}
          </div>
        ) : null}

        {aggregateLoading ? (
          <div className="camera-analysis-loading">Refreshing aggregate dashboard…</div>
        ) : null}

        {/* ================================================================== */}
        {/* MINUTE ANALYSIS                                                     */}
        {/* ================================================================== */}
        <div id="minute" className="camera-analysis-section-title">
          <div>
            <span>MINUTE DATA</span>
            <h2>Minute-wise Count Analysis</h2>
            <p>{aggregateLocationLabel} · {selectedDate}</p>
          </div>
          <div className="camera-latest-badge">Separate from the realtime 30-row limit</div>
        </div>

        <SummaryCards
          totals={minuteData?.summary || emptyTotals()}
          sourceLabel="Full selected-day minute summary"
          totalLabel="Total Minute Count"
        />

        <section className="camera-panel camera-analysis-chart-panel">
          <div className="camera-panel-header">
            <div>
              <h2>Recent Minute Trend</h2>
              <p>Latest 60 available minute buckets for the selected date/location.</p>
            </div>
          </div>
          <TimeBarChart rows={dashboard?.minuteSeries || []} labelMode="minute" />
        </section>

        <AggregateTable
          title="Minute Count Table"
          granularity="minute"
          data={minuteData}
          page={minutePage}
          onPageChange={setMinutePage}
        />

        {/* ================================================================== */}
        {/* HOURLY ANALYSIS                                                     */}
        {/* ================================================================== */}
        <div id="hourly" className="camera-analysis-section-title">
          <div>
            <span>HOURLY DATA</span>
            <h2>Hourly Count Analysis</h2>
            <p>{aggregateLocationLabel} · {selectedDate}</p>
          </div>
        </div>

        <SummaryCards
          totals={hourlyData?.summary || emptyTotals()}
          sourceLabel="Full selected-day hourly summary"
          totalLabel="Total Hourly Count"
        />

        <section className="camera-panel camera-analysis-chart-panel">
          <div className="camera-panel-header">
            <div>
              <h2>Hourly Traffic Profile</h2>
              <p>Combined vehicle count for each hour across the selected location(s).</p>
            </div>
          </div>
          <TimeBarChart rows={dashboard?.hourlySeries || []} labelMode="hour" />
        </section>

        <AggregateTable
          title="Hourly Count Table"
          granularity="hourly"
          data={hourlyData}
          page={hourlyPage}
          onPageChange={setHourlyPage}
        />

        {/* ================================================================== */}
        {/* DAILY ANALYSIS                                                      */}
        {/* ================================================================== */}
        <div id="daily" className="camera-analysis-section-title">
          <div>
            <span>DAILY DATA</span>
            <h2>Daily Count Analysis</h2>
            <p>{aggregateLocationLabel} · {selectedDate}</p>
          </div>
        </div>

        <SummaryCards
          totals={dailyData?.summary || emptyTotals()}
          sourceLabel="Daily aggregate history summary"
          totalLabel="Total Daily Count"
        />

        <AggregateTable
          title="Daily Count Table"
          granularity="daily"
          data={dailyData}
          page={dailyPage}
          onPageChange={setDailyPage}
        />

        {/* ================================================================== */}
        {/* ALWAYS-ALL-LOCATIONS DASHBOARD - independent of location filter.    */}
        {/* ================================================================== */}
        <div id="all-locations" className="camera-analysis-section-title">
          <div>
            <span>ALL LOCATIONS</span>
            <h2>Daily Count Dashboard Across Every Location</h2>
            <p>{selectedDate} · this section always compares all camera locations</p>
          </div>
        </div>

        <SummaryCards
          totals={allLocationDashboard?.totals || emptyTotals()}
          sourceLabel="All locations combined"
          totalLabel="All-location Total"
        />

        <LocationCountAnalysis dashboard={allLocationDashboard} />
        <AllLocationMap dashboard={allLocationDashboard} />
      </main>
    </div>
  );
}
